import { HOA_PERIOD_LABELS, LISTING_STATUS_LABELS } from "./constants";

export const FACT_ROWS = [
  ["listing_status", "Status", (v) => LISTING_STATUS_LABELS[v] || v],
  ["mls_number", "MLS #"],
  ["property_type", "Property Type"],
  ["beds", "Bedrooms"],
  ["baths", "Bathrooms"],
  ["sqft", "Square Feet"],
  ["lot_size", "Lot Size"],
  ["year_built", "Year Built"],
  ["stories", "Stories"],
  ["parking", "Parking"],
  ["heating_cooling", "Heating / Cooling"],
  ["taxes", "Annual Taxes"],
];

export function hoaLineFor(listing) {
  return listing.hoa_fee ? `${listing.hoa_fee}${HOA_PERIOD_LABELS[listing.hoa_period] || ""}` : "";
}

export function DetailsPhotosSection({ ordered, fileUrl, onViewPhoto }) {
  return (
    <section id="gallery" className="scroll-mt-24">
      <h2 className="font-serif-display text-xl sm:text-2xl text-[#202833] mb-5">All Photos</h2>
      {ordered.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {ordered.map((file, i) => (
            <button key={file.id} data-testid={`public-details-photo-${file.id}`} onClick={() => onViewPhoto(i)} className="aspect-square overflow-hidden rounded-lg bg-[#F8F7F4]">
              <img src={fileUrl(file)} alt="" loading="lazy" className="h-full w-full object-cover hover:scale-105 transition-transform duration-300" />
            </button>
          ))}
        </div>
      ) : <p className="text-sm text-[#667085]">Photos coming soon.</p>}
    </section>
  );
}

export function FloorplanSection({ floorplanUrl }) {
  if (!floorplanUrl) return null;
  return (
    <section>
      <h2 className="font-serif-display text-xl sm:text-2xl text-[#202833] mb-5">Floorplan</h2>
      <a href={floorplanUrl} target="_blank" rel="noopener noreferrer" data-testid="public-details-floorplan" className="block w-full max-w-3xl rounded-xl overflow-hidden border border-[#E7E4DE]">
        <img src={floorplanUrl} alt="Floorplan" className="w-full object-contain bg-[#F8F7F4]" />
      </a>
    </section>
  );
}

export function VideoSection({ video, fileUrl }) {
  if (!video) return null;
  return (
    <section>
      <h2 className="font-serif-display text-xl sm:text-2xl text-[#202833] mb-5">Walkthrough Video</h2>
      <div data-testid="public-details-video" className="relative aspect-video max-w-4xl rounded-xl overflow-hidden border border-[#E7E4DE] bg-[#111820]">
        {video.kind === "embed"
          ? <iframe data-testid="public-details-video-embed" src={video.embed_url} title="Walkthrough video" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen className="absolute inset-0 w-full h-full border-0" />
          : <video data-testid="public-details-video-file" controls playsInline className="absolute inset-0 w-full h-full object-contain" src={fileUrl({ id: video.file_id })} />}
      </div>
    </section>
  );
}

export function TourSection({ tourUrl }) {
  if (!tourUrl) return null;
  return (
    <section data-testid="public-details-tour" className="-mx-4 sm:-mx-6 lg:-mx-10">
      <h2 className="font-serif-display text-xl sm:text-2xl text-[#202833] mb-5 px-4 sm:px-6 lg:px-10">3D Virtual Tour</h2>
      <iframe data-testid="public-details-tour-embed" src={tourUrl} title="3D virtual tour" allow="fullscreen; vr; xr-spatial-tracking" allowFullScreen loading="lazy" className="w-full h-[60vh] sm:h-[75vh] border-0" />
    </section>
  );
}

export function FactsSection({ facts, hoaLine, listing }) {
  if (!facts.length) return null;
  return (
    <div>
      <h2 className="font-serif-display text-xl sm:text-2xl text-[#202833] mb-5">Property Facts</h2>
      <dl data-testid="public-details-facts" className="grid sm:grid-cols-2 gap-x-8 gap-y-3 border-t border-[#E7E4DE] pt-4">
        {facts.map(([key, label, format]) => (
          <div key={key} className="flex items-center justify-between border-b border-[#E7E4DE] pb-3">
            <dt className="text-sm text-[#667085]">{label}</dt>
            <dd data-testid={`public-details-fact-${key}`} className="text-sm font-medium text-[#202833]">{format ? format(listing[key]) : listing[key]}</dd>
          </div>
        ))}
        {hoaLine && (
          <div className="flex items-center justify-between border-b border-[#E7E4DE] pb-3">
            <dt className="text-sm text-[#667085]">HOA Fee</dt>
            <dd data-testid="public-details-fact-hoa" className="text-sm font-medium text-[#202833]">{hoaLine}</dd>
          </div>
        )}
      </dl>
    </div>
  );
}

export function HighlightsSection({ highlights }) {
  if (!highlights?.length) return null;
  return (
    <div>
      <h2 className="font-serif-display text-xl sm:text-2xl text-[#202833] mb-5">Highlights</h2>
      <ul data-testid="public-details-highlights" className="grid sm:grid-cols-2 gap-x-8 gap-y-2.5">
        {highlights.map((h, i) => (
          <li key={`${i}-${h}`} data-testid={`public-details-highlight-${i}`} className="flex items-start gap-2 text-sm text-[#202833]"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#C7A361] shrink-0" />{h}</li>
        ))}
      </ul>
    </div>
  );
}

export function DescriptionSection({ description }) {
  if (!description) return null;
  return (
    <div>
      <h2 className="font-serif-display text-xl sm:text-2xl text-[#202833] mb-5">Description</h2>
      <p data-testid="public-details-description" className="text-sm text-[#667085] leading-relaxed whitespace-pre-line">{description}</p>
    </div>
  );
}
